import { request, setAuthToken } from "../http/api-client";
import type { ServerAuthResponse, RefreshTokenResponse } from "../types";
import { authStorage } from "@/utils/auth-storage";

// Token storage keys
const ACCESS_TOKEN_KEY = "finpay-token";
const REFRESH_TOKEN_KEY = "finpay-refresh";
const REMEMBER_KEY = "finpay-remember";

export function getStoredTokens() {
  try {
    return {
      accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
      refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY)
    };
  } catch {
    return { accessToken: null, refreshToken: null };
  }
}

export function storeTokens(accessToken: string, refreshToken?: string) {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  } catch (error) {
    // Silently handle storage errors
  }
}

export function clearStoredTokens() {
  try {
    // Clear from both localStorage and sessionStorage
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    // Silently handle storage errors
  }
}

export function getStoredRememberMePreference(): boolean {
  try {
    const stored = localStorage.getItem(REMEMBER_KEY);
    return stored ? JSON.parse(stored) : false;
  } catch {
    return false;
  }
}

export function storeRememberMePreference(rememberMe: boolean) {
  try {
    localStorage.setItem(REMEMBER_KEY, JSON.stringify(rememberMe));
  } catch (error) {
    // Silently handle storage errors
  }
}

export function clearRememberMePreference() {
  try {
    localStorage.removeItem(REMEMBER_KEY);
  } catch (error) {
    // Silently handle storage errors
  }
}

export async function loginApi(email: string, password: string, rememberMe = false, mfaCode?: string) {
  const body: any = { email, password, rememberMe };
  if (mfaCode) {
    body.mfaCode = mfaCode;
  }

  const res = await request<ServerAuthResponse>("/api/v1/auth/login", {
    method: "POST",
    body,
  });

  const { data } = res;

  // Check if MFA is required
  if (data.mfaRequired) {
    throw {
      success: false,
      data,
      message: "MFA verification required"
    };
  }

  // Set auth header and store both tokens using enhanced storage
  setAuthToken(data.accessToken!);
  const accessTokenResult = authStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken!);
  if (data.refreshToken) {
    const refreshTokenResult = authStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
  }

  // Store remember me preference
  storeRememberMePreference(rememberMe);

  // Debug: Log stored tokens
  console.log('Tokens stored:', {
    accessToken: data.accessToken!,
    refreshToken: data.refreshToken,
    storageUsed: accessTokenResult.usedFallback
  });

  return data;
}

export async function logoutApi(): Promise<{ success: boolean; message?: string }> {
  try {
    const res = await request<{ success: boolean; message: string }>("/api/v1/auth/logout", {
      method: "POST",
    });

    // Clear stored tokens and remember me preference regardless of API response success
    clearStoredTokens();
    clearRememberMePreference();
    setAuthToken(null);

    return { success: true, message: res.message || "Logged out successfully" };
  } catch (error) {
    // Even if API call fails, clear local tokens and remember me preference
    clearStoredTokens();
    clearRememberMePreference();
    setAuthToken(null);

    return {
      success: false,
      message: error instanceof Error ? error.message : "Logout failed"
    };
  }
}

export async function refreshApi(): Promise<{ accessToken: string; refreshToken?: string; expiresIn: number } | null> {
  // Get refresh token using enhanced storage
  const refreshTokenResult = authStorage.getItem<string>(REFRESH_TOKEN_KEY);
  const refreshToken = refreshTokenResult.data;

  console.log('Refresh token found:', refreshToken ? 'YES' : 'NO');
  if (refreshTokenResult.usedFallback) {
    console.log('Using fallback storage for refresh token');
  }

  if (!refreshToken) {
    console.log('No refresh token in storage');
    return null;
  }

  try {
    const res = await request<RefreshTokenResponse>("/api/v1/auth/refresh", {
      method: "POST",
      body: { refreshToken },
    });

    const { data } = res;

    // Update stored tokens with enhanced storage
    setAuthToken(data.accessToken);
    authStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    if (data.refreshToken) {
      authStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    }

    return {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresIn: data.expiresIn
    };
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Clear all tokens on refresh failure
    clearStoredTokens();
    setAuthToken(null);
    return null;
  }
}

export async function validateToken(): Promise<{ user: any } | null> {
  try {
    const res = await request<{ success: boolean; data: { user: any } }>("/api/v1/auth/me", {
      method: "GET",
    });

    if (res.success) {
      return res.data;
    }
    return null;
  } catch (error) {
    return null;
  }
}

